import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../supabase';

interface SidebarProps {
    user: any;
    isMobile?: boolean;
    isOpen?: boolean;
    onClose?: () => void;
}

export default function Sidebar({ user, isMobile = false, isOpen = true, onClose }: SidebarProps) {
    const navigate = useNavigate();
    const location = useLocation();

    const handleLogout = async () => {
        try {
            await supabase.auth.signOut();
            localStorage.removeItem('userRole');
            localStorage.removeItem('studentName');
            localStorage.removeItem('studentGrade');
            navigate('/');
        } catch (error) {
            // Ignore logout errors
        }
    };

    const userRole = localStorage.getItem('userRole');

    const navItems = userRole === 'student' ? [
        { label: 'Dashboard', path: '/student-dashboard', icon: 'dashboard' },
        { label: 'Browse Teachers', path: '/find-teachers', icon: 'group' },
        { label: 'AI Assistant', path: '/ai-assistant', icon: 'psychology' },
        { label: 'Chat History', path: '/chat-history', icon: 'history' },
    ] : [
        { label: 'Dashboard', path: '/dashboard', icon: 'dashboard' },
        { label: 'Find Teachers', path: '/find-teachers', icon: 'group' },
    ];

    const secondaryNav = [
        { label: 'About', path: '/about', icon: 'info' },
        { label: 'Contact', path: '/contact', icon: 'mail' },
    ];

    const renderNavLink = (item: { label: string; path: string; icon: string }) => {
        const isActive = location.pathname === item.path;
        return (
            <Link key={item.path} to={item.path} onClick={() => isMobile && onClose?.()}
                style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', marginBottom: '0.25rem', borderRadius: '10px', textDecoration: 'none', color: isActive ? '#197fe6' : '#64748b', background: isActive ? 'rgba(25, 127, 230, 0.08)' : 'transparent', borderRight: isActive ? '3px solid #197fe6' : '3px solid transparent', fontWeight: isActive ? 700 : 500, fontSize: '0.9rem', transition: 'all 0.2s ease' }}
                onMouseEnter={(e) => { if (!isActive) { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.color = '#0f172a'; } }}
                onMouseLeave={(e) => { if (!isActive) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#64748b'; } }}
            >
                <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>{item.icon}</span>
                <span>{item.label}</span>
            </Link>
        );
    };

    return (
        <aside style={{ width: '288px', minHeight: '100vh', background: '#ffffff', borderRight: '1px solid rgba(0, 0, 0, 0.08)', padding: '1.5rem 1rem', display: 'flex', flexDirection: 'column', position: 'fixed', left: isMobile ? (isOpen ? '0' : '-308px') : 0, top: 0, bottom: 0, overflowY: 'auto', zIndex: isMobile ? 150 : 100, transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)', boxShadow: isMobile && isOpen ? '4px 0 24px rgba(0,0,0,0.12)' : 'none' }}>
            {/* Logo */}
            <Link to="/" style={{ textDecoration: 'none', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#197fe6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span className="material-symbols-outlined" style={{ color: '#fff', fontSize: '22px' }}>school</span>
                </div>
                <span style={{ fontWeight: 800, fontSize: '1.15rem', color: '#0f172a', letterSpacing: '-0.02em' }}>LankaTutor</span>
            </Link>

            {/* User Profile */}
            <div style={{ background: '#f8fafc', borderRadius: '14px', padding: '0.85rem', marginBottom: '1.5rem', border: '1px solid rgba(0, 0, 0, 0.06)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    {user.photoURL ? (
                        <img src={user.photoURL} alt={user.displayName} style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(25, 127, 230, 0.2)' }} />
                    ) : (
                        <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'linear-gradient(135deg, #197fe6, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', fontWeight: 'bold', color: '#fff' }}>
                            {(user.displayName || user.email)?.[0]?.toUpperCase()}
                        </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: '0.85rem', fontWeight: 600, color: '#0f172a', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {user.displayName || 'User'}
                        </p>
                        <p style={{ fontSize: '0.7rem', color: '#94a3b8', margin: '1px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {userRole === 'student' ? 'Student' : 'Teacher'}
                        </p>
                    </div>
                </div>
            </div>

            {/* Navigation */}
            <nav style={{ flex: 1 }}>
                {navItems.map(renderNavLink)}
                <div style={{ height: '1px', background: 'rgba(0, 0, 0, 0.06)', margin: '0.75rem 0' }} />
                {secondaryNav.map(renderNavLink)}
            </nav>

            {/* Logout Button */}
            <button onClick={handleLogout} style={{ width: '100%', padding: '0.75rem 1rem', borderRadius: '10px', border: 'none', background: 'transparent', color: '#ef4444', fontWeight: 500, cursor: 'pointer', transition: 'all 0.2s ease', fontSize: '0.9rem', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '0.75rem' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.05)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>logout</span>
                Logout
            </button>
        </aside>
    );
}
